/**
 * Ambient premium-game stage backdrop — pure Canvas particles + scan grid.
 */

export class GameFxBackdrop {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private dpr = 1;
  private raf = 0;
  private running = false;
  private particles: Array<{ x: number; y: number; vx: number; vy: number; r: number; a: number }> =
    [];
  private t0 = performance.now();
  private pulse = 1;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D unavailable");
    this.ctx = ctx;
    this.resize();
    this.seed();
    window.addEventListener("resize", () => {
      this.resize();
      this.seed();
    });
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    const loop = () => {
      if (!this.running) return;
      this.draw((performance.now() - this.t0) / 1000);
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  /** Brief intensity boost during mode transitions. */
  setPulse(mult: number): void {
    this.pulse = Math.max(0.6, Math.min(2, mult));
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
  }

  private resize(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.floor(w * this.dpr);
    this.canvas.height = Math.floor(h * this.dpr);
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  private seed(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const n = Math.min(80, Math.floor((w * h) / 18000));
    this.particles = Array.from({ length: n }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      vx: (Math.random() - 0.5) * 0.25,
      vy: -0.15 - Math.random() * 0.35,
      r: 0.6 + Math.random() * 1.8,
      a: 0.15 + Math.random() * 0.45,
    }));
  }

  private draw(t: number): void {
    const ctx = this.ctx;
    const w = window.innerWidth;
    const h = window.innerHeight;

    const g = ctx.createLinearGradient(0, 0, w, h);
    g.addColorStop(0, "#061530");
    g.addColorStop(0.4, "#0a2450");
    g.addColorStop(1, "#031022");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);

    // aurora bands
    for (let i = 0; i < 3; i++) {
      const cx = w * (0.2 + i * 0.3) + Math.sin(t * 0.35 + i) * 80;
      const cy = h * (0.15 + i * 0.12);
      const rg = ctx.createRadialGradient(cx, cy, 10, cx, cy, w * 0.35);
      rg.addColorStop(0, i === 1 ? `rgba(255, 210, 100, ${0.06 * this.pulse})` : `rgba(60, 160, 255, ${0.1 * this.pulse})`);
      rg.addColorStop(1, "transparent");
      ctx.fillStyle = rg;
      ctx.fillRect(0, 0, w, h);
    }

    // perspective grid floor
    ctx.save();
    ctx.strokeStyle = "rgba(90, 180, 255, 0.08)";
    ctx.lineWidth = 1;
    const horizon = h * 0.42;
    for (let i = 0; i < 18; i++) {
      const p = i / 17;
      const y = horizon + Math.pow(p, 1.6) * (h - horizon);
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }
    for (let i = -12; i <= 12; i++) {
      ctx.beginPath();
      ctx.moveTo(w / 2 + i * 28, horizon);
      ctx.lineTo(w / 2 + i * 120, h);
      ctx.stroke();
    }
    ctx.restore();

    // hex sparkles / particles
    for (const p of this.particles) {
      p.x += p.vx + Math.sin(t + p.y * 0.01) * 0.05;
      p.y += p.vy;
      if (p.y < -10) {
        p.y = h + 10;
        p.x = Math.random() * w;
      }
      if (p.x < 0) p.x = w;
      if (p.x > w) p.x = 0;
      ctx.fillStyle = `rgba(140, 210, 255, ${p.a})`;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fill();
    }

    // soft vignette
    const v = ctx.createRadialGradient(w / 2, h * 0.4, h * 0.15, w / 2, h / 2, h * 0.9);
    v.addColorStop(0, "transparent");
    v.addColorStop(1, "rgba(0,0,0,0.55)");
    ctx.fillStyle = v;
    ctx.fillRect(0, 0, w, h);
  }
}
